import React from "react";
import * as Tooltip from "@radix-ui/react-tooltip";
import { METRIC_TOOLTIPS, METRIC_LABELS } from "../constants";

/** Small circle-i button; opens the project's white-card Radix tooltip on hover/focus. */
const InfoIcon = ({ label, description }) => (
  <Tooltip.Root>
    <Tooltip.Trigger asChild>
      <button
        type="button"
        className="inline-flex text-gray-400 hover:text-gray-500 transition-colors align-middle"
        aria-label={`${label} info`}
        onClick={(e) => e.stopPropagation()}
      >
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </Tooltip.Trigger>
    <Tooltip.Portal>
      <Tooltip.Content
        side="top"
        align="center"
        sideOffset={6}
        className="max-w-[280px] bg-white border border-gray-200 rounded-md shadow-lg z-50"
      >
        <div className="px-2.5 py-1.5 border-b border-gray-100 bg-primary-50 rounded-t-md">
          <span className="text-xs font-medium text-primary-700">{label}</span>
        </div>
        <div className="px-2.5 py-1.5 text-xs leading-normal text-gray-500">
          {description}
        </div>
        <Tooltip.Arrow className="fill-white" />
      </Tooltip.Content>
    </Tooltip.Portal>
  </Tooltip.Root>
);

/** One "Label: value" pair (optional info icon), kept on a single line. */
const Metric = ({ label, value, tooltip }) => (
  <div className="flex items-center gap-1 whitespace-nowrap">
    <span className="text-gray-500">{label}:</span>
    <span className="font-semibold text-gray-900 tabular-nums">
      {value ?? "–"}
    </span>
    {tooltip && <InfoIcon label={label} description={tooltip} />}
  </div>
);

// Thin pipe separator between metrics.
const Separator = () => (
  <span className="text-gray-300 select-none" aria-hidden="true">
    |
  </span>
);

const RepetitionMetricsCell = ({
  isLoopCompressed = false,
  uniqueActivities,
  repetitionCount,
  repeatedActivities,
  loopsCount = null,
  repetitionsRange = null,
  medianLoopRepeats = null,
}) => (
  <Tooltip.Provider delayDuration={150}>
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      <span className="font-semibold text-gray-700">
        {METRIC_LABELS.SECTION}:
      </span>
      <Metric
        label={METRIC_LABELS.UNIQUE_ACTIVITIES}
        value={uniqueActivities}
        tooltip={METRIC_TOOLTIPS.UNIQUE_ACTIVITIES}
      />
      <Separator />
      <Metric
        label={METRIC_LABELS.REPEATED_ACTIVITIES}
        value={repeatedActivities}
        tooltip={METRIC_TOOLTIPS.REPEATED_ACTIVITIES}
      />
      {isLoopCompressed ? (
        <>
          <Separator />
          <Metric
            label={METRIC_LABELS.LOOPS}
            value={loopsCount}
            tooltip={METRIC_TOOLTIPS.LOOPS}
          />
          <Separator />
          <Metric
            label={METRIC_LABELS.REPETITIONS_RANGE}
            value={
              repetitionsRange
                ? `${repetitionsRange.min}–${repetitionsRange.max}`
                : null
            }
            tooltip={METRIC_TOOLTIPS.REPETITIONS_RANGE}
          />
        </>
      ) : (
        <>
          <Separator />
          <Metric
            label={METRIC_LABELS.REPETITION_COUNT}
            value={repetitionCount}
            tooltip={METRIC_TOOLTIPS.REPETITION_COUNT}
          />
        </>
      )}
      {/* Only present in loop-compressed mode when the variant has loop badges. */}
      {medianLoopRepeats != null && (
        <>
          <Separator />
          <Metric
            label={METRIC_LABELS.MEDIAN_LOOP_REPEATS}
            value={medianLoopRepeats}
            tooltip={METRIC_TOOLTIPS.MEDIAN_LOOP_REPEATS}
          />
        </>
      )}
    </div>
  </Tooltip.Provider>
);

export default RepetitionMetricsCell;
